import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {categories} from '../data/poses';

const {FiCamera,FiUsers,FiStar,FiTrendingUp,FiBriefcase,FiHeart,FiZap,FiImage,FiGrid}=FiIcons;

const categoryIcons={
  business: FiBriefcase,
  senior: FiStar,
  couple: FiHeart,
  creative: FiZap,
  lifestyle: FiCamera,
  fitness: FiTrendingUp,
  artistic: FiImage,
  branding: FiBriefcase
};

// Color mapping for categories
const categoryBackgroundColors={
  business: '#0F0F0F',
  senior: '#1C1C1C', 
  couple: '#2E2A2B',
  creative: '#3B2F2F',
  lifestyle: '#4B3B3F',
  fitness: '#5C4B51',
  artistic: '#6D5C5F',
  branding: '#7E6D6F'
};

function Home() {
  const [selectedCount,setSelectedCount]=React.useState(0);

  React.useEffect(()=> {
    const updateCount=()=> {
      const stored=JSON.parse(localStorage.getItem('selectedPoses') || '[]');
      setSelectedCount(stored.length);
    };
    updateCount();
    window.addEventListener('favoritesUpdated',updateCount);
    return ()=> window.removeEventListener('favoritesUpdated',updateCount);
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header with Logo */}
      <motion.div initial={{y: -30,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{duration: 0.7}} className="text-center mb-12" >
        <div className="flex justify-center items-center mb-6">
          <video src="https://filedn.com/lujmEiF7ALfJ9rDUrehIfEz/MatchaDesign_Posefolio-Logo.mp4" alt="PÓSEFLIO Logo" className="h-48 md:h-64 lg:h-80 w-auto max-w-full" autoPlay muted loop playsInline />
        </div>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          Professional pose reference for photographers. Find the perfect instructions for every shoot.
        </p>
      </motion.div>

      {/* Quick Actions */}
      {selectedCount > 0 && (
        <motion.div 
          initial={{y: 20,opacity: 0}} 
          animate={{y: 0,opacity: 1}} 
          transition={{duration: 0.5,delay: 0.1}} 
          className="mb-8"
        >
          <Link 
            to="/selection" 
            className="block bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="bg-brand-accent/20 p-3 rounded-full mr-4">
                  <SafeIcon icon={FiHeart} className="text-xl text-brand-accent" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-sm">My Favorites</h3>
                  <p className="text-xs text-slate-600">View your selected poses</p>
                </div>
              </div>
              <div className="bg-brand-accent text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                {selectedCount}
              </div>
            </div>
          </Link>
        </motion.div>
      )}

      {/* Categories Grid */}
      <motion.div initial={{y: 30,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{duration: 0.7,delay: 0.2}} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" >
        {categories.map((category,index)=> (
          <motion.div key={category.id} initial={{y: 20,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{duration: 0.5,delay: index * 0.1}} whileHover={{y: -5,scale: 1.02}} whileTap={{scale: 0.98}} >
            <Link to={`/category/${category.id}`} className="block h-full">
              <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden h-full flex flex-col">
                <div className="p-6 text-white flex-grow flex flex-col justify-between min-h-[180px]" style={{backgroundColor: categoryBackgroundColors[category.id]}}>
                  <div className="flex items-center justify-between mb-3">
                    <SafeIcon icon={categoryIcons[category.id]} className="text-3xl opacity-90" />
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                      {category.count} poses
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{category.name}</h3>
                    <p className="text-white/90 text-sm leading-relaxed">
                      {category.description}
                    </p>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center text-slate-600">
                    <SafeIcon icon={FiGrid} className="text-sm mr-2" />
                    <span className="text-sm">Tap to explore poses</span>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>

      {/* Footer Info */}
      <motion.div initial={{opacity: 0}} animate={{opacity: 1}} transition={{duration: 0.7,delay: 0.5}} className="mt-16 text-center" >
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-slate-800 mb-4">Professional Photography Made Simple</h2>
          <p className="text-slate-600 leading-relaxed mb-6 text-sm">
            PÓSEFLIO provides detailed pose instructions to help photographers direct their subjects with confidence. Each category contains 20 carefully crafted poses with professional direction scripts you can use during your photoshoots.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <SafeIcon icon={FiCamera} className="text-3xl text-brand-primary mx-auto mb-3" />
              <h3 className="font-semibold text-slate-800 mb-2 text-sm">Professional Poses</h3>
              <p className="text-xs text-slate-600">Curated poses for every type of shoot</p>
            </div>
            <div className="text-center">
              <SafeIcon icon={FiUsers} className="text-3xl text-brand-accent mx-auto mb-3" />
              <h3 className="font-semibold text-slate-800 mb-2 text-sm">Clear Instructions</h3>
              <p className="text-xs text-slate-600">Detailed directions to guide your subjects</p>
            </div>
            <div className="text-center">
              <SafeIcon icon={FiStar} className="text-3xl text-brand-secondary mx-auto mb-3" />
              <h3 className="font-semibold text-slate-800 mb-2 text-sm">Better Results</h3>
              <p className="text-xs text-slate-600">Create stunning portraits every time</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default Home;